import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useStats,
  useMatchHistory,
  useTournamentHistory,
  useUploadImage,
  useKothHistoryInfinite,
} from "@/lib/hooks/useApi";
import useInfiniteScroll from "@/lib/use-infinite-scroll";
import { usePrivy, type WalletWithMetadata } from "@privy-io/react-auth";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useBalance } from "@/lib/hooks/useBalance";
import DepositSheet from "@/components/DepositSheet";
import WithdrawSheet from "@/components/WithdrawSheet";
import { useExportWallet } from "@privy-io/react-auth/solana";
import { formatAddress, USDC_ICON } from "@/lib/utils";
import { UserName } from "@/components/UserName";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InfoIcon, Copy } from "lucide-react";
import MatchHistory from "@/components/MatchHistory";
import TournamentHistory from "@/components/TournamentHistory";
import HostedTournamentHistory from "@/components/HostedTournamentHistory";
import RushHistory from "@/components/RushHistory";
import WtaHistory from "@/components/WtaHistory";
import KothHistory from "@/components/KothHistory";
import OneVOneHistory from "@/components/OneVOneHistory";
import { useMatchmakingHistory } from "@/lib/hooks/useApi";
import { ProfileImagePopup } from "@/components/ProfileImagePopup";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";

import { usePostHog } from "@posthog/react";
import { SocialLinkDialog } from "@/components/SocialLinkDialog";
import { t } from "@/lib/i18n";

export const Route = createFileRoute("/(game)/profile")({
  component: ProfilePage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { socialLink?: boolean } => {
    // Only return socialLink if it exists in the search params
    if (!("socialLink" in search)) {
      return {};
    }
    const socialLinkValue = search.socialLink;
    // Explicitly check for true values only
    const shouldOpen = socialLinkValue === "true" || socialLinkValue === true;
    return {
      socialLink: shouldOpen,
    };
  },
});

type HistoryType =
  | "matches"
  | "tournaments"
  | "hosted"
  | "rush"
  | "grandRush"
  | "wta"
  | "koth"
  | "oneVOne";
function ProfilePage() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { ready, authenticated, user, logout } = usePrivy();
  const { exportWallet } = useExportWallet();
  const authWalletAddress = user?.wallet?.address;
  const { socialLink } = Route.useSearch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isForceRefresh, setForceRefresh] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [historyView, setHistoryView] = useState<HistoryType>("oneVOne");
  const [socialLinkDialogOpen, setSocialLinkDialogOpen] = useState(false);

  const {
    data: statsData,
    refetch: refetchStatsData,
    isPending,
  } = useStats(authWalletAddress ?? "", isForceRefresh);

  const gameWalletAddress = statsData?.embeddedWallet;
  const userId = statsData?.userId;

  const { mutateAsync: uploadImage } = useUploadImage();

  const {
    data: balanceUsdc,
    isLoading,
    refetch: refetchBalanceUsdc,
  } = useBalance(gameWalletAddress ?? "");

  const { data: matchHistory, refetch: refetchMatchHistory } =
    useMatchHistory(userId);
  const { data: tournamentHistory, refetch: refetchTournamentHistory } =
    useTournamentHistory(userId, "tournament");
  const { data: rushHistory, refetch: refetchRushHistory } =
    useTournamentHistory(userId, "rush");
  const { data: grandRushHistory, refetch: refetchGrandRushHistory } =
    useTournamentHistory(userId, "grand_rush");

  const { data: wtaHistory, refetch: refetchWtaHistory } = useTournamentHistory(
    userId,
    "wta",
  );

  const { data: hostedHistory, refetch: refetchHostedHistory } =
    useTournamentHistory(userId, "user_hosted");

  const {
    data: kothHistoryPages,
    refetch: refetchKothHistory,
    fetchNextPage: fetchNextKothHistory,
    hasNextPage: hasMoreKothHistory,
    isFetchingNextPage: isFetchingNextKothHistory,
    isLoading: isLoadingKothHistory,
  } = useKothHistoryInfinite(userId);

  const { lastElementRef: lastKothElementRef } = useInfiniteScroll({
    isLoading: isLoadingKothHistory,
    isFetchingNextPage: isFetchingNextKothHistory,
    hasNextPage: !!hasMoreKothHistory,
    fetchNextPage: fetchNextKothHistory,
  });

  // 1 Versus 1 (matchmaking) history
  const {
    data: oneVOneHistoryPages,
    refetch: refetchOneVOneHistory,
    fetchNextPage: fetchNextOneVOne,
    hasNextPage: hasMoreOneVOne,
    isFetchingNextPage: isFetchingNextOneVOne,
    isLoading: isLoadingOneVOne,
  } = useMatchmakingHistory(userId, { enabled: userId != null });

  // Refetch 1v1 history on every profile mount so the list reflects current
  // match state without waiting on React Query's staleTime.
  useEffect(() => {
    if (userId == null) return;
    void refetchOneVOneHistory();
  }, [userId, refetchOneVOneHistory]);

  const { lastElementRef: lastOneVOneRef } = useInfiniteScroll({
    isLoading: isLoadingOneVOne,
    isFetchingNextPage: isFetchingNextOneVOne,
    hasNextPage: !!hasMoreOneVOne,
    fetchNextPage: fetchNextOneVOne,
  });

  // pending_create + expired are server-internal queue states (tx confirming
  // or never landed). v0.1 hides them from the user — never render.
  const oneVOneRows = (
    oneVOneHistoryPages?.pages.flatMap((p) => p.rows) ?? []
  ).filter(
    (r) =>
      r.kind !== "queue" ||
      (r.status !== "pending_create" && r.status !== "expired"),
  );

  const matches = Array.isArray(matchHistory) ? matchHistory : [];
  const tournaments = Array.isArray(tournamentHistory) ? tournamentHistory : [];
  const hostedTournaments = Array.isArray(hostedHistory) ? hostedHistory : [];
  const rushTournaments = Array.isArray(rushHistory) ? rushHistory : [];
  const grandRushTournaments = Array.isArray(grandRushHistory)
    ? grandRushHistory
    : [];
  const wtaTournaments = Array.isArray(wtaHistory) ? wtaHistory : [];
  const koths = kothHistoryPages?.pages.flatMap((p) => p.koths) ?? [];

  const balance = balanceUsdc ?? 0;
  const isAuthenticated = ready && authenticated;
  const hasEmbeddedSolanaWallet = !!user?.linkedAccounts?.find(
    (account): account is WalletWithMetadata =>
      account.type === "wallet" &&
      account.walletClientType === "privy" &&
      account.chainType === "solana",
  );

  const connectedVia = useMemo(() => {
    const accounts = user?.linkedAccounts;
    if (!accounts) return null;

    // Check for external wallet first (Phantom, World, etc.)
    // If user logged in via wallet, this is always present
    const externalWallet = accounts.find(
      (a): a is WalletWithMetadata =>
        a.type === "wallet" && a.walletClientType !== "privy",
    );
    if (externalWallet) {
      const name = externalWallet.walletClientType;
      return name ? name.charAt(0).toUpperCase() + name.slice(1) : "Wallet";
    }

    // No external wallet = user logged in via social (X, Google, etc.)
    const google = accounts.find((a) => a.type === "google_oauth");
    if (google) return "Google";
    const twitter = accounts.find((a) => a.type === "twitter_oauth");
    if (twitter) return "X";

    return null;
  }, [user?.linkedAccounts]);

  const handleCopy = async () => {
    if (!gameWalletAddress) return;
    try {
      await navigator.clipboard.writeText(gameWalletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      await uploadImage(file);
      await refetchStatsData();
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const handleExportWallet = async () => {
    try {
      if (!hasEmbeddedSolanaWallet) {
        alert(t("profile.youMustHaveEmbeddedPrivyWallet"));
        return;
      }
      await exportWallet();
    } catch (error) {
      console.error("Failed to export wallet:", error);
      alert(t("profile.failedToExportWallet"));
    }
  };

  useEffect(() => {
    if (gameWalletAddress) {
      refetchBalanceUsdc();
    }
  }, [gameWalletAddress, refetchBalanceUsdc]);

  // Open social link dialog if query parameter is set to true
  useEffect(() => {
    if (socialLink === true) {
      setSocialLinkDialogOpen(true);
      // Remove the query parameter from URL after opening
      navigate({
        to: "/profile",
        search: {},
        replace: true,
      });
    }
  }, [socialLink, navigate]);

  const isLoadingData = isLoading || isPending || !statsData;

  return (
    <div className="bg-[#1D2A52] flex flex-1 flex-col h-full overflow-hidden p-1 gap-4 pb-12">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <ResourceBar
        showBalance
        showLuckyDrawTicket
        showMenu={true}
        className="absolute top-4.5 left-12 right-4 flex z-10 pl-3 space-x-4"
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center min-h-0">
        {/* Avatar Section */}
        {isLoadingData ? (
          <div className="flex-1 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          statsData && (
            <div className="flex-1 flex flex-col p-1 gap-4 w-full min-h-0">
              {/* Identity Section */}
              <div className="flex flex-row items-start gap-4 mt-16">
                <ProfileImagePopup
                  imageUrl={
                    statsData.profilePicture ??
                    `https://avatar.vercel.sh/${gameWalletAddress ?? authWalletAddress}`
                  }
                  activeFrameUrl={statsData.activeFrameUrl}
                  onImageUpload={handleImageUpload}
                  size={115}
                />

                <div className="flex flex-col justify-center min-w-0">
                  <div className="flex items-center gap-2">
                    <UserName
                      username={
                        statsData.username ??
                        gameWalletAddress ??
                        authWalletAddress ??
                        ""
                      }
                      className="font-semibold text-2xl"
                      isVerified={statsData.isVerified}
                      domainType={statsData.domainType}
                      verifiedIconSize="w-5 h-5"
                    />

                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-black/20 hover:bg-black/30 transition-colors align-middle"
                          aria-label="Verification info"
                        >
                          <InfoIcon className="size-4 text-white" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="max-w-xs text-sm text-left">
                        <p>
                          Link Twitter/X and at least 1 other social to get
                          verified and receive weekly rewards.
                        </p>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex flex-row items-center gap-2 mt-0.5">
                    <p className="text-sm text-zinc-400">
                      {!copied ? (
                        formatAddress(gameWalletAddress ?? "")
                      ) : (
                        <span className="text-emerald-400 font-medium inline-flex gap-1.5">
                          {t("profile.addressCopied")}
                          {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                          <img
                            src="https://cdn.navalabs.io/assets/icon_check_JVuGuy.png"
                            alt={t("profile.altTexts.check")}
                            className="size-4"
                          />
                        </span>
                      )}
                    </p>
                    {!copied && (
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="cursor-pointer"
                      >
                        <Copy className="size-3.5 text-zinc-400 hover:text-white transition" />
                      </button>
                    )}
                  </div>

                  {connectedVia && (
                    <p className="text-xs text-zinc-500 mt-1 uppercase">
                      {t("profile.connectedVia")} {connectedVia}
                    </p>
                  )}
                  <SocialLinkDialog />
                </div>
              </div>

              {/* Player Stats - 3 columns */}
              <div className="w-full border-t border-[#495473] pt-4">
                <div className="grid grid-cols-3 gap-4">
                  {/* Balance */}
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src={USDC_ICON}
                      alt={t("profile.altTexts.usd")}
                      className="w-6 h-6"
                    />
                    <span className="text-xs text-zinc-400 uppercase">
                      {t("profile.balance")}
                    </span>
                    <span className="text-xl font-bold text-white">
                      {balance.toFixed(2)}
                    </span>
                  </div>

                  {/* Trophy */}
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src="https://cdn.navalabs.io/icons/trophy-icon_zozppj.png"
                      alt={t("profile.altTexts.trophy")}
                      className="w-6 h-6"
                    />
                    <span className="text-xs text-zinc-400 uppercase">
                      {t("profile.weeklyTrophy")}
                    </span>
                    <span className="text-xl font-bold text-white">
                      {statsData.trophies?.toLocaleString()}
                    </span>
                  </div>

                  {/* Rank */}
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <img
                        src="https://cdn.navalabs.io/assets/rank_VhfVGe.png"
                        alt={t("profile.altTexts.star")}
                        className="w-full h-full object-contain"
                        style={{
                          imageRendering: "pixelated",
                        }}
                      />
                    </div>
                    <span className="text-xs text-zinc-400 uppercase">
                      {t("profile.weeklyRank")}
                    </span>
                    <span className="text-xl font-bold text-white">
                      #{statsData.rank}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-full flex gap-2">
                {/* Deposit Button */}
                <DepositSheet
                  balance={balance}
                  walletAddress={gameWalletAddress}
                  onSuccess={refetchBalanceUsdc}
                >
                  <div className="flex-1 relative cursor-pointer">
                    <img
                      src="https://cdn.navalabs.io/assets/deposit_fFMtar.png"
                      alt={t("profile.altTexts.deposit")}
                      className="w-full h-auto"
                    />
                    <span className="absolute inset-0 flex items-center justify-start pl-4 text-white font-semibold text-sm">
                      {t("profile.deposit")}
                    </span>
                  </div>
                </DepositSheet>

                {/* Withdraw Button */}
                <WithdrawSheet balance={balance} onSuccess={refetchBalanceUsdc}>
                  <div className="flex-1 relative cursor-pointer">
                    <img
                      src="https://cdn.navalabs.io/assets/withdraw_cyOnwD.png"
                      alt={t("profile.altTexts.withdraw")}
                      className="w-full h-auto"
                    />
                    <span className="absolute inset-0 flex items-center justify-start pl-4 text-white font-semibold text-sm">
                      {t("profile.withdraw")}
                    </span>
                  </div>
                </WithdrawSheet>

                {/* Export PK Button */}
                <button
                  className="flex-1 relative bg-transparent hover:opacity-80 transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleExportWallet}
                  disabled={!isAuthenticated || !hasEmbeddedSolanaWallet}
                >
                  <img
                    src="https://cdn.navalabs.io/assets/export-pk_MYlxuw.png"
                    alt={t("profile.altTexts.exportPk")}
                    className="w-full h-auto"
                  />
                  <span className="absolute inset-0 flex items-center justify-start pl-4 text-white font-semibold text-sm">
                    {t("profile.exportPk")}
                  </span>
                </button>
              </div>

              {/* Select and History */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <Select
                  value={historyView}
                  onValueChange={(value) =>
                    setHistoryView(value as HistoryType)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select view" />
                  </SelectTrigger>
                  <SelectContent className="text-center">
                    <SelectItem value="oneVOne">1v1</SelectItem>
                    <SelectItem value="rush">{t("profile.rush")}</SelectItem>
                    {/* <SelectItem value="grandRush">
                      {t("profile.grandRush")}
                    </SelectItem> */}
                    <SelectItem value="wta">
                      {t("profile.winnerTakesAll")}
                    </SelectItem>
                    {/* <SelectItem value="tournaments">
                      {t("profile.tournament")}
                    </SelectItem> */}
                    {/* <SelectItem value="hosted">
                      {t("profile.alphaDome")}
                    </SelectItem> */}
                    <SelectItem value="koth">
                      {t("profile.kingOfTheHill")}
                    </SelectItem>
                    {/* <SelectItem value="matches">
                      {t("profile.practice")}
                    </SelectItem> */}
                  </SelectContent>
                </Select>

                <div className="flex-1 min-h-0">
                  {/* {historyView === "matches" ? (
                    <MatchHistory
                      matches={matches}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchMatchHistory={refetchMatchHistory}
                    />
                  ) : null} */}
                  {historyView === "tournaments" ? (
                    <TournamentHistory
                      tournaments={tournaments}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchTournamentHistory={refetchTournamentHistory}
                    />
                  ) : historyView === "hosted" ? (
                    <HostedTournamentHistory
                      tournaments={hostedTournaments}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchTournamentHistory={refetchHostedHistory}
                    />
                  ) : historyView === "rush" ? (
                    <RushHistory
                      tournaments={rushTournaments}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchRushHistory={refetchRushHistory}
                    />
                  ) : historyView === "grandRush" ? (
                    <RushHistory
                      tournaments={grandRushTournaments}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchRushHistory={refetchGrandRushHistory}
                      isGrandRush
                    />
                  ) : historyView === "wta" ? (
                    <WtaHistory
                      tournaments={wtaTournaments}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchWtaHistory={refetchWtaHistory}
                    />
                  ) : historyView === "oneVOne" ? (
                    <OneVOneHistory
                      rows={oneVOneRows}
                      walletAddress={gameWalletAddress ?? ""}
                      hasMore={!!hasMoreOneVOne}
                      isFetchingNextPage={isFetchingNextOneVOne}
                      lastElementRef={lastOneVOneRef}
                      onRefetch={() => refetchOneVOneHistory()}
                    />
                  ) : (
                    <KothHistory
                      koths={koths}
                      walletAddress={gameWalletAddress ?? ""}
                      refetchKothHistory={refetchKothHistory}
                      onLoadMore={fetchNextKothHistory}
                      hasMore={!!hasMoreKothHistory}
                      isFetchingNextPage={isFetchingNextKothHistory}
                      lastElementRef={lastKothElementRef}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
